import { useContext, useEffect, useState, useRef } from "react";
import { useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { useOutletContext } from "react-router";
import styled from "styled-components";
import { AppQueryClient } from "../../../api/queryClient";
import AppContext from "../../../AppContext";
import { APP_TITLE } from "../../../config/constant";
import { defaultStatusColors } from "../../../utils/CardUtils";
import { formatDefaultDateTime } from "../../../utils/Formatter";
import * as Library from "../../../utils/Library";
import { roleAccesse } from "../../../utils/roleManager";
import InfinityGridLabelBrandRequestsCards from "../../common/cardContainers/InfinityGridLabelBrandRequestsCards";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import ListComponent from "../../common/lists/ListComponent";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import MainWindowHeader, { CustomSearch } from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";

// #region  SCOPED CSS
const StyledPage = styled.div`
    & {
        overflow-y: scroll;
        height: 100%;
        margin-right: -23px;
        margin-left: -24px;
        padding-left: 24px;
        padding-right: 24px;
    }
`;
// #endregion

export default function LabelBrandsRequestsPage() {
    document.title = `${APP_TITLE} - Label Brands Requests`;

    // #region VARS
    const [onRemoveDialog, setRemoveDialog] = useState(false);
    const [onResendDialogLoading, setResendDialogLoading] = useState(false);
    const queryClient = useQueryClient();
    const gridRef = useRef(null);

    const [inviteID, setInviteID] = useState("");
    const [showRequestedAlreadyDialog, setShowRequestedAlreadyDialog] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const { MainLayoutProps, layoutType } = useOutletContext();
    const [isLoading, setIsLoading] = useState(false);

    const navigations = useSelector(state => state.navigationAccesses);
    const { pageRefreshFromNotification, setPageRefreshFromNotification, userData } = useContext(AppContext);

    const userRole = userData?.role;
    const editAccess = roleAccesse(userRole, "retail brand account admin");
    const [viewLayoutStyle, setViewLayoutStyle] = useState("Grid");
    const [listNumRows, setListNumRows] = useState(50);

    //---------- Header Filters
    const [searchText, setSearchText] = useState("");
    //-----
    const [allRetailBrandsList, setAllRetailBrandsList] = useState([]);
    const [filteredRetailBrand, setFilteredRetailBrand] = useState([]);
    //-----
    const [allStatusList, setAllStatusList] = useState([
        { name: "Requested", value: "Invited", selected: true },
        // { name: "Accepted", value: "Accepted", selected: true }, // this invite status is NOT to show
        { name: "Declined", value: "Declined", selected: true },
        { name: "Revoked", value: "Voided", selected: true }
    ]);
    const [filteredStatus, setFilteredStatus] = useState([]);
    //-----
    const [requestEnabled, setRequestsEnabled] = useState(false);

    const [showVoidConfirmationDialog, setShowVoidConfirmationDialog] = useState({
        show: false,
        inviteID: "",
        brandName: "",
        wasAccepted: false
    });

    const filters = {
        filterRetailBrandIDs: filteredRetailBrand?.map(item => item?.value),
        filterStatuses: filteredStatus?.map(item => item?.value),
        searchText: searchText
    };
    const infiniteGridQuerykey = ["labelBrandsRequestsInfiniteGrid", filters];
    const listQueryKey = ["labelBrandsRequestsList", filters];

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    // #endregion

    // #region LIST COMPONENT PROPS
    const listColumns = [
        {
            name: "Request sent to",
            selector: row => row?.labelBrand?.name ?? "--"
        },
        {
            name: "BOtag ID",
            selector: row => row?.ourID ?? "--"
        },
        {
            name: "Requested on",
            selector: row => formatDefaultDateTime(row?.createdAt) ?? "--"
        },
        {
            name: "Accepted on",
            selector: row => (row?.statusLog ? formatDefaultDateTime(row?.statusLog[0]?.date) : "--")
        },
        {
            name: "Contact person",
            selector: row => (row?.status === "Accepted" ? row?.contactFirstName : (row?.contactFirstName ?? "--"))
        },
        {
            name: "Contact email",
            selector: row => (row?.status === "Accepted" ? row?.contactEmail : (row?.contactEmail ?? "--"))
        },
        {
            name: "Requested stores",
            selector: row => row?.requestedStores ?? "--"
        },
        {
            name: "Accepted stores",
            selector: row => row?.acceptedStores ?? "--"
        },
        {
            name: "To brand",
            selector: row => row?.retailBrand?.name ?? "--"
        }
    ];
    // #endregion

    // #region METHODS
    const onRetailBrandChange = selection => {
        setFilteredRetailBrand([selection]);

        const currentRetailBrandID = selection?.value;
        sessionStorage.setItem("currentRetailBrand", currentRetailBrandID || "");
    };

    const onStatusChange = (selections, dropdownDataWithSelections) => {
        setFilteredStatus(selections);
        setAllStatusList(dropdownDataWithSelections);
    };

    const onFiltersLoaded = data => {
        const defaultRetailBrand =
            sessionStorage.getItem("currentRetailBrand") || localStorage.getItem("defaultRetailBrand");

        if (data?.retailBrands?.length) {
            const retailBrands = data?.retailBrands?.map((item, index) => {
                const defaultRetailBrandID = localStorage.getItem("defaultRetailBrand");
                let retailBrandName = item?.name;

                if (item?._id === defaultRetailBrandID) {
                    retailBrandName = `${item?.name} (default)`;
                }
                return {
                    name: retailBrandName,
                    value: item?._id,
                    selected: item?._id === defaultRetailBrand
                };
            });

            const defaultRetailBrandObj =
                retailBrands?.find(item => item?.value === defaultRetailBrand) || retailBrands?.[0];

            setAllRetailBrandsList(retailBrands);
            setFilteredRetailBrand([defaultRetailBrandObj]);
        }

        setRequestsEnabled(true);
    };

    const onClickRefreshAction = () => {
        if (props?.layoutType === "List") AppQueryClient.invalidateQueries(listQueryKey);
        else {
            //else AppQueryClient.invalidateQueries(infiniteGridQuerykey);
            gridRef.current?.reset?.(infiniteGridQuerykey);
        }
    };

    const handleResendRequest = async data => {
        setIsLoading(true);
        const retailBrandName = data?.retailBrand?.name || "RetailBrand";
        const labelBrandName = data?.labelBrand?.name || "--";

        await Library.makePostRequest(
            "resendInviteToLabelBrand",
            { inviteID: data?._id },
            false,
            setIsLoading,
            undefined,
            undefined,
            false
        ).then(res => {
            const { status, message } = res?.data;
            if (status === 200) {
                Library.showToastMessage({
                    type: "success",
                    title: "Request resent!",
                    message: `You just resent a request from ${retailBrandName} to ${labelBrandName}`
                });
            } else if (status === 401) {
                setShowRequestedAlreadyDialog(true);
                setErrorMessage(message);
            }
        });
    };
    const handleRemoveRequest = async () => {
        setResendDialogLoading(true);
        await Library.makeCommonPostRequest(
            "Invites",
            "hideInvite",
            {
                inviteID: inviteID
            },
            false,
            setResendDialogLoading
        ).then(res => {
            if (res?.status?.data?.status === 200) {
                Library.showToastMessage({
                    type: "success",
                    title: "Successfully removed!",
                    message: "The invitation card was removed successfully"
                });
            }
            handleCloseDialog();
            onClickRefreshAction();
        });
    };

    const handleOpenDialog = inviteID => {
        setInviteID(inviteID);
        setRemoveDialog(true);
    };

    const onRevokeRequest = async data => {
        setShowVoidConfirmationDialog({ show: true, inviteID: data?._id, brandName: data?.labelBrand?.name });
    };

    const handleVoidInvitationRequest = async inviteID => {
        const res = await Library.makeCommonPostRequest(
            "Invites",
            "updateInvite",
            {
                inviteID: inviteID,
                status: "Voided"
            },
            false,
            setIsLoading
        );

        if (res?.data?.status === 200) {
            Library.showToastMessage({
                type: "success",
                title: "Invitation revoked!",
                message: "This invitation is no longer valid."
            });

            onClickRefreshAction();
        }
    };

    const handleCloseDialog = () => {
        setRemoveDialog(false);
        setInviteID("");
    };
    const handleCloseDialogBox = () => {
        setShowRequestedAlreadyDialog(false);
        setErrorMessage("");
        onClickRefreshAction();
    };

    const onSearch = searchText => {
        setSearchText(searchText);
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        setViewLayoutStyle(props?.layoutType);

        setupHeaders(
            props,
            onClickRefreshAction,
            navigations,
            allStatusList,
            allRetailBrandsList,
            onRetailBrandChange,
            onStatusChange,
            onFiltersLoaded,
            requestEnabled,
            filteredRetailBrand,
            filteredStatus,
            onSearch,
            editAccess
        );
    }, [
        props?.layoutType,
        allRetailBrandsList,
        filteredRetailBrand,
        allStatusList,
        filteredStatus,
        requestEnabled,
        editAccess
    ]);

    useEffect(() => {
        if (pageRefreshFromNotification) {
            console.log("pageRefreshFromNotification", pageRefreshFromNotification);
            setPageRefreshFromNotification(false);
            onClickRefreshAction();
        }
    }, [pageRefreshFromNotification]);

    useEffect(() => {
        Library.removeQueries(queryClient, infiniteGridQuerykey);
        Library.removeQueries(queryClient, listQueryKey);
    }, []);

    // #endregion

    // #region RENDER
    return (
        <StyledPage>
            <FullScreenLoader isLoading={isLoading} />

            <FullScreenOverlay show={onRemoveDialog}>
                <DialogBoxComponent
                    loading={onResendDialogLoading}
                    heading={onResendDialogLoading ? "REMOVING......" : "JUST CHECKING..."}
                    title="Ready to remove?"
                    message="Removing the invite only removes this card and does not remove the connection."
                    headingColor={onResendDialogLoading ? "var(--text-icons-green-500)" : "var(--text-icons-blue-500)"}
                    onClickYes={handleRemoveRequest}
                    onClickNo={handleCloseDialog}
                />
            </FullScreenOverlay>
            <FullScreenOverlay show={showRequestedAlreadyDialog}>
                <DialogBoxComponent
                    heading={"ATTENTION..."}
                    title="REQUEST ALREADY ACCEPTED"
                    message={errorMessage}
                    headingColor={"var(--text-icons-green-500)"}
                    showCancel={false}
                    showConfirm={true}
                    confirmText="CLOSE"
                    onClickYes={handleCloseDialogBox}
                />
            </FullScreenOverlay>
            <FullScreenOverlay show={showVoidConfirmationDialog.show}>
                <DialogBoxComponent
                    heading="JUST CHECKING"
                    headingColor="var(--text-icons-red-500)"
                    title="Ready to revoke invitation?"
                    message="Do you want the invitation to be canceled? The retailer will receive a 'This invitation is no longer valid' notification."
                    onClickYes={() => {
                        handleVoidInvitationRequest(showVoidConfirmationDialog?.inviteID);
                        setShowVoidConfirmationDialog({ show: false, inviteID: "", brandName: "" });
                    }}
                    loading={isLoading}
                    loadingHeading={"Voiding..."}
                    onClickNo={() => {
                        setShowVoidConfirmationDialog({ show: false, inviteID: "", brandName: "" });
                    }}
                    cancelText="No"
                    confirmText="Yes"
                />
            </FullScreenOverlay>
            <StyledPage>
                {viewLayoutStyle === "Grid" && (
                    <InfinityGridLabelBrandRequestsCards
                        ref={gridRef}
                        minColumnWidth="370px"
                        limit="16"
                        queryKey={infiniteGridQuerykey}
                        onResendRequest={handleResendRequest}
                        onRemoveRequest={handleOpenDialog}
                        onRevokeRequest={onRevokeRequest}
                        filters={filters}
                        requestEnabled={requestEnabled}
                    />
                )}
                {viewLayoutStyle === "List" && (
                    <ListComponent
                        title="LABEL BRANDS"
                        //title2="(LAST 10 INVITED)"
                        columns={listColumns}
                        api={{
                            endpoint: "getRequestsToLabelBrands",
                            formData: {
                                companyID: localStorage.getItem("currentCompanyID"),
                                page: 1,
                                limit: listNumRows,
                                ...filters
                            }
                            // onSuccess: mapListData
                        }}
                        pageLimit={listNumRows}
                        currentPage={1}
                        queryKey={listQueryKey}
                        actions={{
                            includeActions: true,
                            editIsVisible: false,
                            suspendIsVisible: false,
                            // removeIsVisible: false,
                            extraActions: [
                                {
                                    title: "REVOKE",
                                    onClick: row => {
                                        onRevokeRequest(row);
                                    },
                                    isDisabled: row => {
                                        return (
                                            row?.status !== "Requested" &&
                                            row?.status !== "Invited" &&
                                            row?.status !== "Pending"
                                        );
                                    }
                                },
                                {
                                    title: "RESEND",
                                    onClick: row => {
                                        handleResendRequest(row);
                                    },
                                    isDisabled: row => {
                                        return (
                                            row?.status !== "Requested" &&
                                            row?.status !== "Invited" &&
                                            row?.status !== "Pending"
                                        );
                                    }
                                }
                            ]
                        }}
                        includeStatus={{
                            stickyStatus: true,
                            statusFromPath: "status",
                            statusColors: defaultStatusColors
                        }}
                        sortByCreatedAt={true}
                        requestEnabled={requestEnabled}
                        onChangeRowsPerPage={rowsPerPage => setListNumRows(rowsPerPage)}
                    />
                )}
            </StyledPage>
        </StyledPage>
    );
    // #endregion
}

// #region FUNCTIONS
function setupHeaders(
    props,
    onClickRefreshAction,
    navigations,
    allStatusList,
    allRetailBrandsList,
    onRetailBrandChange,
    onStatusChange,
    onFiltersLoaded,
    requestEnabled,
    filteredRetailBrand,
    filteredStatus,
    onSearch,
    editAccess
) {
    const createMode = navigations?.navigationAccesses?.labelbrands?.createModeOnly;

    props.setControlBarLeftBtns([
        { customSearch: <CustomSearch enabled={true} onTextChange={onSearch} placeholder="Search" /> }
    ]);
    props.setControlBarCenterBtns([
        { to: "/labelbrands/connections", active: false, disabled: createMode, title: "CONNECTIONS" },
        { to: "/labelbrands/invitations", active: false, disabled: false, title: "INVITATIONS" },
        { to: "/labelbrands/request", active: true, disabled: false, title: "REQUESTS" }
    ]);
    props.setControlBarRightBtns([
        { to: "#", active: true, disabled: createMode, title: "View", icon: "eye" },
        {
            to: editAccess ? "/labelbrands/request" : "#",
            active: false,
            disabled: !editAccess,
            title: "Request",
            icon: "mail"
        },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    //TODO: Change this to a "Request" button
    props.setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: true
                }}
                onRefreshClick={onClickRefreshAction}
                firstOrganize={
                    Library.isAdminDebug()
                        ? {
                              visible: true,
                              dropdownData: allRetailBrandsList,
                              onDropdownChange: onRetailBrandChange,
                              disabled: false,
                              type: "singleSelect",
                              selectedValue: filteredRetailBrand?.[0]?.name
                          }
                        : {
                              visible: true,
                              disabled: true
                          }
                }
                secondOrganize={{
                    visible: true,
                    dropdownData: allStatusList,
                    onDropdownChange: onStatusChange,
                    type: "multiSelect",
                    multidropdownOptions: {
                        selectAllText: "Select all status",
                        labelAllSelected: "All status",
                        labelNoneSelected: "--",
                        allItems: allStatusList
                    }
                }}
                pageFilters={{
                    useFilters: true,
                    filters: {
                        useRetailBrands: true,
                        useConnectedLabelBrands: true
                    },
                    onFiltersLoaded: onFiltersLoaded
                }}
                search={{
                    visible: false
                }}
            />
        )
    });
}
// #endregion
